import {
  DownloadOutlined,
  LoadingOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { Button, Flex, Form, Input, Modal, Radio, Tooltip } from "antd";
import React, { FC, useEffect, useState } from "react";
import OPDMoreActions from "../opd-billing/opd-more-actions/OPDMoreActions";
import { IDeficiencyCheck } from "../../interfaces/IPatient";
import TextArea from "antd/es/input/TextArea";
import { IDocumentFields } from "../../interfaces/ICase";
import { postQuestionDeficiencyCheck } from "../../apis/patientService";
import { errorMessage, successMessage } from "../../utils/notificationUtils";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { postDeficiencyQuestion } from "../../models/patientSlice";
import { setUploadedDocumentUrl } from "../../models/preAuthClaimSlice";
import { setUploadedDocumentUrl as enhancementDocuments } from "../../models/EnhancementSlice";
import Loader from "../../utils/Spinners";
import { errorMessageModal } from "../../utils/modalMessage";

type DocumentDeficiencyProps = {
  deficiencyModal: boolean;
  documentUrl: string;
  closeDeficiencyModal: any;
  documentName?: string;
  handleDownloadDocument?: CallableFunction;
  isDownloadingDocument?: boolean;
  downloadingDocumentUrl?: string;
  deficiencyQuestions?: any;
  selectedDocument?: IDocumentFields | null;
  setUploadedDocumentUrl?: any;
  type?: string;
};

const DocumentDeficiencyModal: FC<DocumentDeficiencyProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const isPdf = props?.documentUrl?.toLowerCase().endsWith(".pdf");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [deficiencyStatus, setDeficiencyStatus] = useState<boolean>(false);
  const { preAuthClaim, enhancement, patients } = useSelector(
    (state: RootState) => state
  );

  const isLoading = !props?.deficiencyQuestions;

  const handleZoomIn = () => {
    setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
  };
  const handleZoomOut = () => {
    setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  };

  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
  }, [props?.documentUrl]);

  const [form] = Form.useForm();

  useEffect(() => {
    const initialValues: any = {};
    props?.deficiencyQuestions?.documentsDeficiencyMappingList?.forEach(
      (question: any) => {
        initialValues[question?.documentsDeficiencyChecklistId] = [
          "true",
          true,
        ].includes(question?.status)
          ? "true"
          : ["false", false].includes(question?.status)
          ? "false"
          : null;
      }
    );
    form.setFieldsValue(initialValues);
  }, [props?.deficiencyQuestions, form]);

  useEffect(() => {
    setDeficiencyStatus(
      props?.deficiencyQuestions?.deficiencyCheckStatus || false
    );
  }, [props?.deficiencyQuestions]);

  const onFinish = async () => {
    try {
      await form.validateFields();

      const values = form.getFieldsValue();

      const updatedValues = {
        remarks: values?.remarks,
        documentId: props?.selectedDocument?.id,
        documentDeficiencyStatus: deficiencyStatus,
        documentMasterId: props?.selectedDocument?.documentType
          ? parseInt(props?.selectedDocument?.documentType)
          : null,
        visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id || null,
        documentsDeficiencyMappingRequests: Object.keys(values)
          ?.filter((key) => key !== "remarks")
          ?.map((value) => {
            if (props?.selectedDocument?.documentType) {
              return {
                documentId: props?.selectedDocument?.id,
                documentMasterId: parseInt(
                  props?.selectedDocument?.documentType
                ),
                documentsDeficiencyChecklistId: parseInt(value),
                status: values[value] === "true" ? true : false,
              };
            }
          }),
      };

      const res: any = await dispatch(postDeficiencyQuestion(updatedValues));

      if (res) {
        props?.closeDeficiencyModal();
        successMessage("Deficiency Check submitted Successfully");
        form.resetFields();
        if (props?.setUploadedDocumentUrl) {
          props?.setUploadedDocumentUrl((prev: any) =>
            prev?.map((item: IDocumentFields) =>
              item?.id === props?.selectedDocument?.id
                ? {
                    ...item,
                    deficiencyCheckStatus: res?.payload?.deficiencyCheckStatus,
                  }
                : item
            )
          );
        } else if (props?.type === "claim") {
          const newUploadedDocumentUrl = preAuthClaim?.uploadedDocumentUrl?.map(
            (item) =>
              item?.id === props?.selectedDocument?.id
                ? {
                    ...item,
                    deficiencyCheckStatus: res?.payload?.deficiencyCheckStatus,
                  }
                : item
          );
          dispatch(setUploadedDocumentUrl(newUploadedDocumentUrl));
        } else if (props?.type === "enhancement") {
          const newUploadedDocumentUrl = enhancement?.uploadedDocumentUrl?.map(
            (item) =>
              item?.id === props?.selectedDocument?.id
                ? {
                    ...item,
                    deficiencyCheckStatus: res?.payload?.deficiencyCheckStatus,
                  }
                : item
          );
          dispatch(enhancementDocuments(newUploadedDocumentUrl));
        }
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessageModal("Please fill all details before submitting");
      } else {
        console.error(err);
        props?.closeDeficiencyModal();
        errorMessage(err?.response?.data || "Please, Try again after sometime");
        // form.resetFields();
      }
    }
  };

  return (
    <>
      {props?.documentUrl && (
        <div className="right-side-modal">
          <Modal
            open={props?.deficiencyModal}
            width={4000}
            onCancel={props?.closeDeficiencyModal}
            footer={(_) => (
              <div
                style={{
                  display: "flex",
                  justifyContent: isPdf ? "flex-end" : "space-between",
                }}
              >
                {!isPdf ? (
                  <div className="zoom-controls">
                    <Button
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.5}
                      style={{ marginRight: "0.5rem" }}
                    >
                      <ZoomOutOutlined />
                    </Button>
                    <Button onClick={handleZoomIn} disabled={zoomLevel >= 3}>
                      <ZoomInOutlined />
                    </Button>
                    <Button
                      onClick={handleRotateLeft}
                      style={{ marginLeft: 8 }}
                    >
                      <RotateLeftOutlined />
                    </Button>
                    <Button
                      onClick={handleRotateRight}
                      style={{ marginLeft: 8 }}
                    >
                      <RotateRightOutlined />
                    </Button>
                  </div>
                ) : (
                  ""
                )}
                <div
                  className="footer-button"
                  style={{ display: "flex", gap: "0.4rem" }}
                >
                  {props?.deficiencyQuestions?.deficiencyCheckStatus != null ? (
                    <></>
                  ) : (
                    props?.deficiencyQuestions && (
                      <Button
                        type="primary"
                        htmlType="submit"
                        onClick={onFinish}
                      >
                        Save
                      </Button>
                    )
                  )}

                  <Button key="back" onClick={props?.closeDeficiencyModal}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            style={{ top: 50, right: 0, height: "100vh" }}
            wrapClassName="right-modal"
          >
            <Flex justify="center" align="center">
              <div
                className="document-uploaded"
                style={{ width: "50%", borderRight: "1px solid lightgray" }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      marginRight: "1rem",
                      fontWeight: "600",
                      fontSize: "1rem",
                    }}
                  >
                    {props?.documentName}
                  </span>
                  {props?.isDownloadingDocument ? (
                    <LoadingOutlined />
                  ) : (
                    <Tooltip title="DOWNLOAD" color="blue">
                      <DownloadOutlined
                        style={{
                          color: "#273881",
                          fontSize: "18px",
                        }}
                        onClick={() => {
                          props?.handleDownloadDocument &&
                            props?.handleDownloadDocument(
                              props?.documentName,
                              props?.documentUrl
                            );
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
                {isPdf ? (
                  <object
                    style={{ height: "100vh", marginTop: "2rem" }}
                    data={props?.documentUrl}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                  >
                    <p>
                      Unable to display the file.{" "}
                      <a
                        href={props?.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Click here to view it
                      </a>
                      .
                    </p>
                  </object>
                ) : (
                  <>
                    <div
                      style={{
                        overflow: "auto",
                        width: "100%",
                        height: "100vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        marginTop: "2rem",
                      }}
                    >
                      <img
                        src={props?.documentUrl}
                        style={{
                          transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                          transformOrigin: "top center",
                          transition: "transform 0.3s ease",
                          maxWidth: "100%",
                          maxHeight: "100%",
                          // objectFit: "contain",
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
              <div
                className="content-deficiency-check"
                style={{
                  width: "50%",
                  overflow: "auto",
                  paddingLeft: "0.5rem",
                }}
              >
                {isLoading ? (
                  <Loader />
                ) : (
                  <>
                    <div
                      className="deficiency-options"
                      style={{ height: "100vh", overflow: "auto" }}
                    >
                      <h4>Deficiency Check</h4>
                      <Form
                        name="deficiency-options"
                        autoComplete="off"
                        form={form}
                        labelAlign="left"
                        labelWrap
                        labelCol={{ span: 16 }}
                      >
                        {props?.deficiencyQuestions &&
                          Array.isArray(
                            props?.deficiencyQuestions
                              ?.documentsDeficiencyMappingList
                          ) &&
                          props?.deficiencyQuestions?.documentsDeficiencyMappingList?.map(
                            (question: any) => (
                              <Form.Item
                                key={question?.documentsDeficiencyChecklistId}
                                label={`Question - ${question.question} `}
                                // 
                                name={question?.documentsDeficiencyChecklistId}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please input answer!",
                                  },
                                ]}
                              >
                                <Radio.Group
                                  defaultValue={
                                    ["true", true].includes(question?.status)
                                      ? "true"
                                      : ["false", false].includes(
                                          question?.status
                                        )
                                      ? "false"
                                      : null
                                  }
                                  disabled={
                                    props?.deficiencyQuestions
                                      ?.deficiencyCheckStatus == null
                                      ? false
                                      : true
                                  }
                                >
                                  <Radio value={"true"}> Yes </Radio>
                                  <Radio value={"false"}> No </Radio>
                                </Radio.Group>
                              </Form.Item>
                            )
                          )}
                        <Form.Item label="Remarks" name="remarks">
                          <Input
                            defaultValue={
                              props?.deficiencyQuestions?.remarks || ""
                            }
                            placeholder={
                              props?.deficiencyQuestions?.remarks || ""
                            }
                            disabled={
                              props?.deficiencyQuestions
                                ?.deficiencyCheckStatus == null
                                ? false
                                : true
                            }
                          />
                        </Form.Item>
                      </Form>
                    </div>

                    {
                      // props?.deficiencyQuestions?.responseFromMl == true &&
                      props?.deficiencyQuestions?.deficiencyCheckStatus ==
                      null ? (
                        <div className="deficiency-buttons">
                          <Button
                            block
                            type={deficiencyStatus ? "primary" : undefined}
                            style={{
                              border: "1px solid #1677ff",
                              color: !deficiencyStatus ? "#1677ff" : "white",
                            }}
                            htmlType="submit"
                            onClick={() => setDeficiencyStatus(true)}
                          >
                            DEFICIENCY PASSED
                          </Button>
                          <Button
                            block
                            type={!deficiencyStatus ? "primary" : undefined}
                            danger
                            htmlType="submit"
                            style={{ marginTop: "0.6rem" }}
                            onClick={() => setDeficiencyStatus(false)}
                          >
                            DEFICIENCY FAILED
                          </Button>
                        </div>
                      ) : (
                        <div className="deficiency-buttons">
                          {["true", true].includes(
                            props?.deficiencyQuestions?.deficiencyCheckStatus
                          ) ? (
                            <Button
                              block
                              type={deficiencyStatus ? "primary" : undefined}
                              style={{
                                border: "1px solid #1677ff",
                                color: "#07be6",
                              }}
                              htmlType="submit"
                            >
                              DEFICIENCY PASSED
                            </Button>
                          ) : (
                            <Button
                              block
                              type={!deficiencyStatus ? "primary" : undefined}
                              danger
                              htmlType="submit"
                              style={{ marginTop: "0.6rem" }}
                            >
                              DEFICIENCY FAILED
                            </Button>
                          )}
                        </div>
                      )
                    }
                  </>
                )}
              </div>
            </Flex>
          </Modal>
        </div>
      )}
    </>
  );
};

export default DocumentDeficiencyModal;
